// Escalation — pulling a human in. Always logged with category + reason.
// Categories split into:
//   - HARDCODED_ALWAYS: not user-disable-able, always email-immediate
//   - USER_EXTENSIBLE_ALWAYS: maintainer-extensible, defaults from policy
//   - ASK_BEFORE_ACTING: bot drafts but doesn't execute
//   - SATISFYING / OPERATIONAL: informational, may route to digest
//   - ROUTINE_DEFERRAL: agent-emitted "hand to a human" deferrals, digest-routed
//
// Source: `docs/idea/escalation.md`.

import { z } from 'zod';
import { EscalationIdSchema, RepoIdSchema, SessionIdSchema } from '../value-objects/ids';
import { NotificationKind } from './notification';

/**
 * Hardcoded escalate-always categories. A maintainer who opts out of these
 * opted out of being a maintainer. Enforced even if `.maintainer.yml` tries
 * to drop them.
 */
export const HARDCODED_ALWAYS = ['security', 'license', 'cla'] as const;
export type HardcodedAlways = (typeof HARDCODED_ALWAYS)[number];

/** Built-in user-extensible categories (maintainer adds via policy). */
export const USER_EXTENSIBLE_ALWAYS = ['hostile_tone', 'direct_mention', 'paid_customer'] as const;

/** Drafted-then-confirmed actions. */
export const ASK_BEFORE_ACTING = [
  'breaking_change',
  'major_dep_bump',
  'first_time_contributor',
  'large_pr',
  'release_publish',
  'auto_merge',
] as const;

/** Informational satisfying events. */
export const SATISFYING = [
  'release_shipped',
  'pr_merged_first_time_contributor',
  'milestone_reached',
  'weekly_streak',
] as const;
export type Satisfying = (typeof SATISFYING)[number];

/** Operational escalations the agent may emit. */
export const OPERATIONAL = [
  'cost_cap_reached',
  // `step_cap_reached` WAS HERE and was deleted 2026-09-19, when `packages/domain/src`
  // joined the agent-budgets corpus (`scripts/agent-budgets.corpus.ts`) and the ban that
  // has covered this exact string since #4046 could finally see it. `docs/idea/agent.md`
  // already said the member survived "as type surface with no producer in `runSession`";
  // prod agreed — 2,214 `escalations` rows across 20 categories since 2026-07-22 and not
  // one of them this one, so nothing can fail to parse for its absence. A category no
  // code emits is a branch nobody runs, and the string is what `prompt-hygiene.test.ts`
  // already refuses to let a prompt teach a model to name.
  'provider_failure',
  'byok_invalid',
  'tool_failure',
  'guardrail_violation',
  'monthly_quota_reached',
  'expectation_accounting',
  'platform_tooling_issue',
  'credits_exhausted',
  /**
   * The PR-babysit driver handed a pull request to a human (#4167) — the ONE
   * category behind every member of {@link EscalationReason} the driver can escalate
   * (`apps/worker/src/babysit/driver.ts`). The reason itself is not lost: it
   * rides the row as the structured `payload.babysit_reason`, which is also the
   * dedupe key, so an operator groups by
   * `payload->>'babysit_reason'` and the category stays what it is for — the
   * ROUTING axis.
   *
   * `OPERATIONAL` is the honest group: "the bot stopped, the maintainer needs
   * to know" (`hardcoded.ts` § decideChannel), which is exactly a parked PR.
   * Group membership is the whole channel decision — `decideChannel` reads
   * `new Set(OPERATIONAL)`, so this member routes `email-immediate` with no
   * edit in `@developerz/agent`, and that is what the babysit lane writes on
   * the row it inserts.
   *
   * Three tempting homes, each rejected for a reason worth keeping:
   *
   * - `auto_merge` (ASK_BEFORE_ACTING) for `auto_merge_disabled` /
   *   `human_merge_lane`. ASK_BEFORE_ACTING means the bot DRAFTED and will act
   *   on approval. These two are the opposite: the repo's own config forbids us
   *   ever merging this PR, and no approval unblocks it. Filing them there
   *   would promise an approval path that does not exist.
   * - `ci_timeout` (ROUTINE_DEFERRAL) for `ci_wait_timeout`. The prose fits, the
   *   CHANNEL does not: {@link ROUTINE_DEFERRAL}'s own docblock says these
   *   "must route to the weekly DIGEST, never email-immediate". A babysit park
   *   is mailed immediately (`NotificationKind.Escalation` forces email), so a
   *   `ci_timeout` row would have to claim a channel its group forbids.
   * - `platform_tooling_issue` (OPERATIONAL). A red check after three spent
   *   self-fix attempts is the repo's CI, not our tooling; that name would send
   *   an operator hunting a platform fault that is not there.
   */
  'pr_babysit_parked',
] as const;
export type Operational = (typeof OPERATIONAL)[number];

/**
 * Agent-emitted ROUTINE deferrals the loop PROMPTS instruct the model to fire via
 * `session.escalate({ category: … })` (`prompts/issue-loop.md` + `pr-loop.md`).
 * They are healthy "hand this to a human" outcomes, NOT operational faults — so
 * they must route to the weekly DIGEST, never email-immediate: deliberately kept
 * OUT of `OPERATIONAL` (whose set forces email in the channel router). Before
 * dz#906 they were absent from every group, so `EscalationCategorySchema.safeParse`
 * dropped them in `findAcceptedEscalationCategory` and the session outcome recorded
 * `unknown` for a perfectly normal deferral.
 *
 * - `low_confidence` — a scoped plan below `triage.confidence_threshold`; defer
 *   rather than dispatch a shaky plan to the coding agent.
 * - `ci_timeout`     — a check run that ended `timed_out` (the only sender is
 *   `apps/runner/src/.../pr-loop.md`, per the `pr-loop` escape). The "CI never settled"
 *   case the babysit lane parks on is `ci_wait_timeout`, which routes to OPERATIONAL
 *   and emails immediately (#6292 — `pr_babysit_parked`).
 */
export const ROUTINE_DEFERRAL = ['low_confidence', 'ci_timeout'] as const;

/**
 * Every escalation category, flat, across all groups — the COMPLETE vocabulary a
 * caller may name. Derived from the same group tuples the schema is built from,
 * so it can never drift from `EscalationCategorySchema` (guarded in the test).
 *
 * This is the closed set `session.escalate` enforces at the tool boundary (#776):
 * a category outside it is a model hallucination (`tool_input_unavailable` was one
 * observed live), and enforcing it here is what stops the invented string from
 * being persisted to the `escalations` row and then downgraded to `unknown` at the
 * terminal audit row.
 */
export const ESCALATION_CATEGORIES = [
  ...HARDCODED_ALWAYS,
  ...USER_EXTENSIBLE_ALWAYS,
  ...ASK_BEFORE_ACTING,
  ...SATISFYING,
  ...OPERATIONAL,
  ...ROUTINE_DEFERRAL,
] as const;

// A single flat enum over every group, not a union of per-group enums: the
// accepted set and the inferred type are identical, but a rejection reads
// `Invalid option: expected one of "security"|…|"ci_timeout"` — the full
// vocabulary the caller (and the tool.call.err audit row) needs to self-correct,
// instead of zod's opaque union error.
export const EscalationCategorySchema = z.enum([...ESCALATION_CATEGORIES]);
export type EscalationCategory = z.infer<typeof EscalationCategorySchema>;

/**
 * The notification kind each category is EMITTED AS — every escalation producer
 * emits the kind this record maps (#5692). Two index the record directly:
 * `packages/agent/src/escalation/dispatch.ts` (keyed on the session's category)
 * and `apps/worker/src/babysit/escalation.ts` (keyed on
 * `pr_babysit_parked`). The single-category producers spell the mapped kind:
 * `packages/agent/src/triage/escalate-notice.ts`,
 * `apps/worker/src/consumers/repo-visibility.ts`,
 * `apps/worker/src/cron/stalled-queue.ts`,
 * `apps/worker/src/cron/repo-quiet-alarm.ts` and
 * `apps/worker/src/dispatch/seat-cap.ts` (all `decision_needed`); and
 * `apps/worker/src/babysit/marker-link.ts` routes per lane — `platform_notice`
 * on the App lane, `decision_needed` on a `user_token` repo (the demo seed in
 * `packages/db/src/seeds/notifications.ts` writes rows, it emits nothing). Total on
 * purpose: a category added to any group above fails to compile here until
 * someone decides who hears about it. The `escalations` table holds every
 * category; the `escalation` KIND is what this record maps only the three
 * `HARDCODED_ALWAYS` categories to — the one kind whose email an owner or admin
 * cannot switch off (`isLockedChannel`).
 *
 * | categories | kind |
 * |---|---|
 * | `security`, `license`, `cla` | `escalation` |
 * | the four `SATISFYING` | `good_news` |
 * | `expectation_accounting`, `platform_tooling_issue`, `tool_failure` | `platform_notice` |
 * | every other one | `decision_needed` |
 */
export const ESCALATION_CATEGORY_KIND: Record<EscalationCategory, NotificationKind> = {
  security: NotificationKind.Escalation,
  license: NotificationKind.Escalation,
  cla: NotificationKind.Escalation,
  hostile_tone: NotificationKind.DecisionNeeded,
  direct_mention: NotificationKind.DecisionNeeded,
  paid_customer: NotificationKind.DecisionNeeded,
  breaking_change: NotificationKind.DecisionNeeded,
  major_dep_bump: NotificationKind.DecisionNeeded,
  first_time_contributor: NotificationKind.DecisionNeeded,
  large_pr: NotificationKind.DecisionNeeded,
  release_publish: NotificationKind.DecisionNeeded,
  auto_merge: NotificationKind.DecisionNeeded,
  release_shipped: NotificationKind.GoodNews,
  pr_merged_first_time_contributor: NotificationKind.GoodNews,
  milestone_reached: NotificationKind.GoodNews,
  weekly_streak: NotificationKind.GoodNews,
  cost_cap_reached: NotificationKind.DecisionNeeded,
  provider_failure: NotificationKind.DecisionNeeded,
  byok_invalid: NotificationKind.DecisionNeeded,
  tool_failure: NotificationKind.PlatformNotice,
  guardrail_violation: NotificationKind.DecisionNeeded,
  monthly_quota_reached: NotificationKind.DecisionNeeded,
  expectation_accounting: NotificationKind.PlatformNotice,
  platform_tooling_issue: NotificationKind.PlatformNotice,
  credits_exhausted: NotificationKind.DecisionNeeded,
  pr_babysit_parked: NotificationKind.DecisionNeeded,
  low_confidence: NotificationKind.DecisionNeeded,
  ci_timeout: NotificationKind.DecisionNeeded,
};

/**
 * The dedupe-key PREFIX each category-less escalation producer wrote, and the kind
 * its history rows re-kind to (#5692). The producers here emitted kind
 * `escalation` before the category/kind split; their rows carry no
 * `payload.category`, so history cannot be re-kindled by category — the prefix
 * names the producer instead. `migration 0299_notification_rekind` is the SQL
 * side of THIS record (its second statement's `starts_with` arms), held to it by
 * `packages/db/src/notification-rekind-migration.integration.test.ts`, which
 * plants one row per prefix and compares.
 *
 * `ask_escalated-` is why the SQL uses `starts_with`, never LIKE: `_` is a LIKE
 * wildcard, and a key that merely LOOKS like the triage prefix
 * (`askXescalated-…`) must not match.
 *
 * The authorship-mismatch producer is the one prefix with TWO outcomes: the App
 * lane's body carries {@link AUTHORSHIP_MISMATCH_APP_LANE_MARK} and its rows
 * re-kind to `platform_notice`; the default here is the `user_token` lane's.
 * The record maps the default; the exception rides the body sentence, the only
 * lane discriminator those historical rows carry (their payload is null).
 */
export const ESCALATION_DEDUPE_PREFIX_KIND: Readonly<Record<string, NotificationKind>> = {
  'authorship-mismatch-': NotificationKind.DecisionNeeded,
  'stalled-queue-': NotificationKind.DecisionNeeded,
  'seat-capped-': NotificationKind.DecisionNeeded,
  'repo-requires-upgrade-': NotificationKind.DecisionNeeded,
  'ask_escalated-': NotificationKind.DecisionNeeded,
};

/**
 * The one sentence only the App lane's authorship-mismatch body carries —
 * `apps/worker/src/babysit/marker-link.ts` writes it in the App lane's remediation
 * and not in the `user_token` lane's. Historical rows name no lane, so this
 * substring IS the lane discriminator the re-kind reads; held equal to the
 * migration's `position(... in body)` arm by the same integration test as
 * {@link ESCALATION_DEDUPE_PREFIX_KIND}.
 */
export const AUTHORSHIP_MISMATCH_APP_LANE_MARK = 'the lane ran on a personal credential';

/**
 * The categories that describe a fault of the platform's own. Read off
 * {@link ESCALATION_CATEGORY_KIND}, so the set and the record cannot disagree.
 * These three are emitted as the kind `platform_notice` (#5692): a refused email
 * cell and an inbox row, no customer email, and the hidden tier's matrix row is off
 * on every channel with `setPref` refusing an override. The operator-facing rollup
 * over them is slice 04's (#5688).
 */
export const PLATFORM_INTERNAL_CATEGORIES: ReadonlySet<EscalationCategory> = new Set(
  ESCALATION_CATEGORIES.filter(
    (category) => ESCALATION_CATEGORY_KIND[category] === NotificationKind.PlatformNotice,
  ),
);

export const EscalationChannel = {
  EmailImmediate: 'email-immediate',
  Digest: 'digest',
} as const;
export const EscalationChannelSchema = z.enum(EscalationChannel);
export type EscalationChannel = z.infer<typeof EscalationChannelSchema>;

/**
 * What the delivery leg of an escalation actually DID — the row's own answer to
 * "did a human get this, and if not, why not".
 *
 * `delivered_at` alone could not answer it. NULL collapsed five different
 * outcomes: nobody had a route, SMTP threw, the weekly digest owns it, the row
 * predates any writer at all, and "a writer exists and never reported". Measured
 * on prod 2026-09-01: **1,421 rows, every single one `delivered_at IS NULL`** —
 * 1,166 email-immediate and 255 digest — so the column said exactly nothing
 * about 100% of the table, and each of those five states needs a DIFFERENT
 * operator action.
 *
 * | state            | who writes it                         | what the operator does        |
 * |------------------|---------------------------------------|-------------------------------|
 * | `pending`        | the DB default, at insert             | find the leg that never reported |
 * | `delivered`      | the fan-out's aggregate, when any channel sent; the weekly digest's `markDigestEntriesDelivered`, flipping the rows the sent digest carried | nothing |
 * | `collapsed`      | the fan-out's aggregate, when a copy was suppressed because the account's one routed copy went; the babysit lane for a self-clearing reason; the bundle lane for an ask whose PR was answered or re-armed while its email waited (`refuseDeferredEmail` `answered`) | nothing |
 * | `no_route`       | the fan-out's aggregate, when nothing could receive it | check the account has a recipient with an address or a device |
 * | `send_failed`    | the fan-out's aggregate, when a channel faulted; the babysit lane when its enqueue threw; the dispatcher (`@developerz/agent` `escalation/dispatch.ts`) when its fan-out enqueue (`fanout`) or digest bucket (`bucketForDigest`) threw, or its `escalation.fired` (`auditFired`) or `escalation.repeated` (`recordRepeat`) append threw | look at SMTP, the queue or the audit store |
 * | `digest_pending` | the digest bucket leg                 | nothing until Monday; older than a week = a dead digest worker |
 * | `unknown`        | migration 0178, backfilling pre-column rows | nothing — no writer ever existed |
 * | `bundle_pending` | the fan-out, when an email cell was deferred | nothing for an hour; older than a day = a dead bundle lane |
 * | `opted_out`      | the fan-out or the bundle lane               | nothing — every recipient switched this email off |
 *
 * `pending` and `unknown` are deliberately distinct. `pending` means a writer
 * exists and owed this row an answer, so a `pending` row is a live dead-producer
 * alarm; `unknown` means the row predates the column, so it can never be one.
 * Folding the 1,421 historical rows into `pending` would have made the alarm
 * permanently, uselessly red — and folding them into `delivered` would have
 * asserted 1,421 deliveries that provably never happened.
 */
export const EscalationDeliveryState = {
  Pending: 'pending',
  Delivered: 'delivered',
  Collapsed: 'collapsed',
  NoRoute: 'no_route',
  SendFailed: 'send_failed',
  DigestPending: 'digest_pending',
  Unknown: 'unknown',
  /**
   * The same condition was already told inside the refile window (plan 101 N3):
   * this row is the record of a re-fire, and it reached nobody. Its own state, not
   * `collapsed` — that one means a copy was suppressed because another copy went, and
   * an operator must be able to count the two apart. Not reached: the FIRST telling
   * carries the stamp.
   */
  Repeat: 'repeat',
  /**
   * The escalation's email is waiting in a recipient's bundle (`cron.email_bundle`)
   * and no other channel reached anyone. Not reached: the bundle lane flips it to
   * `delivered` when the bundle goes. Older than a day means a dead bundle lane.
   */
  BundlePending: 'bundle_pending',
  /**
   * Nobody was told because every recipient had this email switched off, by their own
   * setting or their role's default. Not reached, and nothing for an operator to fix.
   */
  OptedOut: 'opted_out',
} as const;
export const EscalationDeliveryStateSchema = z.enum(EscalationDeliveryState);
export type EscalationDeliveryState = z.infer<typeof EscalationDeliveryStateSchema>;

/**
 * The whole delivery vocabulary, flat — the source of truth the DB CHECK
 * (`escalations_delivery_state_ck`) is held to by SET EQUALITY in
 * `packages/db/src/schema.test.ts`. Adding a member here without widening the
 * constraint fails the build rather than an INSERT, and so does widening the
 * constraint past what this declares.
 */
export const ESCALATION_DELIVERY_STATES = EscalationDeliveryStateSchema.options;

/**
 * Which states mean a human was actually REACHED — the single seam deciding
 * whether `escalations.delivered_at` gets stamped.
 *
 * `collapsed` counts: a copy was suppressed because another copy went, so the
 * maintainer was told (the fan-out suppresses a copy because the account's one
 * routed copy went; the babysit lane suppresses a self-clearing reason). It
 * is a separate state from `delivered` anyway, because "a send went out about
 * this incident" and "a copy was suppressed beside one that went" are not the
 * same claim and an operator counting real sends must be able to
 * subtract the second. (The routed summary mail that once collapsed several
 * incidents into one send is deleted — #5689.)
 *
 * One seam, because two would drift: `markEscalationDelivery` stamps the
 * timestamp iff this returns true, and `insertIssueEscalationOnce`'s
 * open-incident guard (`delivered_at IS NULL`) reads the result of that stamp.
 */
export function escalationDeliveryReached(state: EscalationDeliveryState): boolean {
  return state === EscalationDeliveryState.Delivered || state === EscalationDeliveryState.Collapsed;
}

export const EscalationSchema = z.object({
  id: EscalationIdSchema,
  repoId: RepoIdSchema,
  sessionId: SessionIdSchema.nullable(),
  category: EscalationCategorySchema,
  reason: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  channel: EscalationChannelSchema,
  deliveryState: EscalationDeliveryStateSchema,
  deliveredAt: z.date().nullable(),
  ackAt: z.date().nullable(),
  ackBy: z.string().nullable(),
  createdAt: z.date(),
});
export type Escalation = z.infer<typeof EscalationSchema>;

/** Set of HARDCODED_ALWAYS for fast membership tests. */
export const HARDCODED_ALWAYS_SET: ReadonlySet<EscalationCategory> = new Set<EscalationCategory>(
  HARDCODED_ALWAYS,
);

/**
 * Narrows to the categories that may never be routed anywhere but
 * `email-immediate` (`channelAllowedForCategory` is the rule; this is the
 * predicate it and every caller test membership with, so the set is read from
 * `HARDCODED_ALWAYS` rather than restated).
 */
export function isHardcodedAlways(category: EscalationCategory): category is HardcodedAlways {
  return HARDCODED_ALWAYS_SET.has(category);
}

/**
 * Invariant: a `HARDCODED_ALWAYS` category MUST route to `email-immediate`.
 * No exceptions, ever. See escalation.md "Hard rules".
 */
export function channelAllowedForCategory(
  category: EscalationCategory,
  channel: EscalationChannel,
): boolean {
  if (isHardcodedAlways(category)) return channel === EscalationChannel.EmailImmediate;
  return true;
}

/**
 * Resolve the required channel for a category. Hardcoded categories pin to
 * immediate; everything else is up to the caller's policy (defaulting to
 * digest is a reasonable choice but not enforced here).
 */
export function requiredChannelFor(category: EscalationCategory): EscalationChannel | null {
  return isHardcodedAlways(category) ? EscalationChannel.EmailImmediate : null;
}
